import { Lock } from "lucide-react";
import { useNavigate } from "react-router";

import { formatDate, formatMoney } from "@/lib/format";
import { useFollowedCompanies } from "@/hooks/queries/useFollowedCompanies";
import { CompanyInitials } from "@/components/companies/CompanyInitials";

const CARD = "rounded-2xl bg-card p-5 shadow-sm";

/** El chip "+2 NUEVAS" de una empresa seguida con adjudicaciones de esta semana. */
export function NewAwardsChip({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
      +{count} {count === 1 ? "nueva" : "nuevas"}
    </span>
  );
}

/** La buena pro va por día: "hoy", "ayer", "hace 3 días" o la fecha. */
function awardedLabel(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "hoy";
  if (days === 1) return "ayer";
  if (days < 7) return `hace ${days} días`;
  return formatDate(iso);
}

/**
 * La columna izquierda de Empresas: las que sigue el usuario y lo último que
 * ganaron. Cada obra va con su propio candado: bloqueada se ve la entidad y el
 * monto; la nomenclatura, al desbloquearla.
 */
export function CompaniesSidebar() {
  const navigate = useNavigate();
  const { companies, activity } = useFollowedCompanies();

  return (
    <div className="flex flex-col gap-4 lg:sticky lg:top-24">
      <div className={CARD}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-bold">Empresas que sigues</h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {companies.length}
          </span>
        </div>
        {companies.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Sigue a una empresa para enterarte cada vez que gane una licitación.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-1">
            {companies.map((company) => (
              <button
                key={company.companyId}
                type="button"
                onClick={() => navigate(`/companies/${company.companyId}`)}
                className="-mx-2 flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-accent"
              >
                <CompanyInitials name={company.name} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-extrabold uppercase" title={company.name}>
                    {company.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Última: {company.lastAwardAt ? formatDate(company.lastAwardAt) : "—"}
                  </div>
                </div>
                <NewAwardsChip count={company.newAwards} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={CARD}>
        <h2 className="text-base font-bold">Actividad reciente</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Adjudicaciones de las empresas que sigues
        </p>
        {activity.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">Todavía no hay adjudicaciones.</p>
        ) : (
          <div className="mt-3 flex flex-col divide-y">
            {activity.map((award) => (
              <button
                key={`${award.companyId}-${award.contractingId}`}
                type="button"
                onClick={() => navigate(`/contracting/${award.contractingId}`)}
                className="flex cursor-pointer gap-2.5 py-3 text-left first:pt-1 last:pb-0"
              >
                <span className="mt-1.5 size-2 shrink-0 rounded-full bg-emerald-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-bold uppercase">{award.companyName}</span> ganó
                    una nueva licitación
                  </p>
                  <p
                    className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground"
                    title={award.nomenclature ?? award.entity ?? undefined}
                  >
                    {award.isLocked && <Lock className="size-3 shrink-0" />}
                    <span className="truncate">
                      {[award.nomenclature, award.entity].filter(Boolean).join(" · ")}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs">
                    <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {formatMoney(award.amount)}
                    </span>
                    <span className="text-muted-foreground"> · {awardedLabel(award.awardedAt)}</span>
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
